import { BookOpen, ChevronLeft, ChevronRight, Edit3, FileIcon, Heart, Image as ImageIcon, Trash2, Video, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import DescricaoEditor from "@/features/comunicacao/components/DescricaoEditor";
import MentionRenderer from "@/features/comunicacao/components/MentionRenderer";
import { useDraft } from "@/features/sistema/hooks/useDraft";
import { supabase } from "@/features/sistema/services/supabaseClient";

import { isTaskLine, normalizeAllToGFM, parseTaskLine, toggleTaskAt } from "../utils/taskList";

// ==== Miniatura de PDF =======================================================
let __pdfWorkerReady = false;
async function ensurePdfWorker() {
  if (__pdfWorkerReady) return;
  // @ts-ignore - Vite resolve para URL do worker
  const workerSrc = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  const pdf = await import('pdfjs-dist');
  // @ts-ignore
  pdf.GlobalWorkerOptions.workerSrc = workerSrc;
  __pdfWorkerReady = true;
}

function PdfThumb({ src, className = "" }: { src: string; className?: string }) {
  const [thumb, setThumb] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensurePdfWorker();
        const pdfMod: any = await import('pdfjs-dist');
        const loadingTask = pdfMod.getDocument(src);
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        const targetW = 220;
        const scale = Math.max(0.2, targetW / viewport.width);
        const vp = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas ctx null');
        canvas.width = Math.max(1, Math.floor(vp.width));
        canvas.height = Math.max(1, Math.floor(vp.height));
        await page.render({ canvasContext: ctx, viewport: vp }).promise;
        const url = canvas.toDataURL('image/png');
        if (!cancelled) setThumb(url);
      } catch (e) {
        if (!cancelled) setThumb(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [src]);

  if (thumb) return <img src={thumb} className={className} alt="PDF thumbnail" />;
  return (
    <div className={`w-full h-16 bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-[11px] font-medium ${className}`}>
      {loading ? 'Carregando…' : 'PDF'}
    </div>
  );
}
// ============================================================================

export type UIAnexo = { url: string; nome?: string; tipo?: string }; // aceita 'foto' | 'arquivo' também
export type UIComment = {
  id: string;
  createdAt: string | Date;
  authorName: string;
  authorId?: string | null;
  content?: string | null;
  type?: string;                       // 'texto' | 'imagem' | 'video' | 'documento' | 'outro' | 'foto' | 'arquivo'
  anexos?: UIAnexo[];                  // array de anexos já enviados
  likedByMe?: boolean;
  likesCount?: number;
  mentionBadge?: boolean;
};

// ---- Lightbox interno (lazy) ----
const isPdf = (url: string) => /\.pdf(\?|$)/i.test(url);
const Lightbox = ({ itens, index, onClose, onChange }:{
  itens: Required<ReturnType<typeof normalizeAnexos>>;
  index: number;
  onClose: () => void;
  onChange: (i:number)=>void;
}) => {
  const atual = itens[index];
  if (!atual) return null;
  const fname = atual.nome || atual.url.split("/").pop() || "arquivo";
  const prev = () => onChange((index - 1 + itens.length) % itens.length);
  const next = () => onChange((index + 1) % itens.length);

  return (
    <div className="mt-2 rounded-md border border-gray-200 dark:border-gray-700 bg-background dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center justify-between px-2 py-1 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 min-w-0">
          {atual.tipo === "imagem" ? <ImageIcon className="w-4 h-4 text-blue-600" /> :
           atual.tipo === "video" ? <Video className="w-4 h-4 text-purple-600" /> :
           <FileIcon className="w-4 h-4 text-gray-600" />}
          <span className="text-xs truncate text-gray-700 dark:text-white">{fname}</span>
        </div>
        <div className="flex items-center gap-1">
          {itens.length > 1 && (
            <>
              <button onClick={prev} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={next} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><ChevronRight className="w-4 h-4" /></button>
            </>
          )}
          <a href={atual.url} target="_blank" rel="noreferrer" className="px-2 py-1 text-xs rounded border text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">Abrir</a>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="p-2">
        {atual.tipo === "imagem" && <img src={atual.url} alt={fname} className="w-full max-h-[60vh] object-contain" />}
        {atual.tipo === "video" && <video src={atual.url} controls className="w-full max-h-[60vh] object-contain bg-black" />}
        {(atual.tipo === "documento" || atual.tipo === "outro") && (
          isPdf(atual.url) ? (
            <iframe
              src={atual.url}
              className="w-full h-[60vh] bg-white"
              title={fname}
            />
          ) : (
            <div className="p-3 text-sm">
              <p className="mb-2 text-gray-700 dark:text-gray-300">Pré-visualização indisponível.</p>
              <a href={atual.url} target="_blank" rel="noreferrer" className="underline text-blue-600 dark:text-blue-400">Abrir em nova aba</a>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// --- normalização de anexos ('foto' -> 'imagem', 'arquivo' -> 'documento') ---
function normalizeAnexos(anexos?: UIAnexo[]) {
  const norm = (t?: string): "imagem"|"video"|"documento"|"outro" => {
    if (t === "foto") return "imagem";
    if (t === "arquivo") return "documento";
    if (t === "imagem" || t === "video" || t === "documento") return t;
    return "outro";
  };
  return (anexos ?? []).map(a => ({ ...a, tipo: norm(a.tipo) }));
}

export default function CommentCard({
  c,
  canEdit,
  canDelete,
  // NOVO: permite toggle de tasks mesmo sem poder editar o texto do comentário
  canToggleTasks = true,
  // callbacks de integração com a página (para manter regras de negócio e permissões no nível da página)
  onSaveEdit,      // recebe texto novo (será usado também para persistir o toggle)
  onDelete,        // já faz o soft-delete / removed_at na página
  onSendToDiary,   // callback para enviar comentário ao diário de obra
  // configurações opcionais
  confirmOnDelete = true,
  className = "",
}: {
  c: UIComment;
  canEdit: boolean;
  canDelete: boolean;
  canToggleTasks?: boolean;
  onSaveEdit: (novoTexto: string) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
  onSendToDiary?: () => void;
  confirmOnDelete?: boolean;
  className?: string;
}) {
  const dataTxt = typeof c.createdAt === "string" ? new Date(c.createdAt).toLocaleString() : c.createdAt.toLocaleString();

  // edição inline (estado local com draft persistente usando useDraft)
  const [isEditing, setIsEditing] = useState(false);
  
  // ✅ Hook useDraft para salvamento local robusto
  type CommentEditDraft = { text: string };
  const { value: draftValue, setValue: setDraftValue, loadDraft, scheduleSave, clearDraft } =
    useDraft<CommentEditDraft>({ 
      key: isEditing ? `comment-edit-${c.id}` : null, 
      initial: { text: c.content || "" } 
    });
  
  const draft = draftValue.text;
  const setDraft = useCallback((text: string) => {
    setDraftValue({ text });
    scheduleSave({ text });
  }, [scheduleSave, setDraftValue]);

  // like (otimista) — usa sua tabela tarefas_comentarios_curtidas
  const [liked, setLiked]   = useState(!!c.likedByMe);
  const [likes, setLikes]   = useState(c.likesCount ?? 0);
  const [liking, setLiking] = useState(false);

  // estado local para conteúdo com checkboxes interativos
  const [contentLocal, setContentLocal] = useState<string>(() => normalizeAllToGFM(c.content || ""));

  // cache do ID do usuário atual (usuarios.id, não auth.user.id)
  const [currentUsuarioId, setCurrentUsuarioId] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      const email = data.user?.email;
      if (!email) return;
      const { data: rows, error } = await supabase
        .from("usuarios")
        .select("id")
        .eq("email", email)
        .limit(1);
      if (error) return;
      const row = Array.isArray(rows) ? rows[0] : null;
      setCurrentUsuarioId(row?.id ?? null);
    })().catch(() => {});
  }, []);

  // ✅ Carregar rascunho quando entrar em modo de edição
  useEffect(() => {
    if (isEditing) {
      (async () => {
        const local = await loadDraft();
        if (!local || !local.text) {
          // sem draft salvo: inicializa com o conteúdo atual
          setDraft(c.content || "");
        }
      })();
    }
  }, [isEditing, loadDraft, c.content, setDraft]);

  // ✅ Limpar rascunho quando sair do modo de edição sem salvar
  useEffect(() => {
    return () => {
      if (!isEditing) {
        clearDraft();
      }
    };
  }, [isEditing, clearDraft]);

  // sincroniza com prop externa
  useEffect(() => {
    setContentLocal(normalizeAllToGFM(c.content || ""));
  }, [c.content]);

  async function toggleLike() {
    if (liking) return;
    setLiking(true);
    // otimista
    setLiked(v => !v);
    setLikes(n => (liked ? Math.max(0, n - 1) : n + 1));
    try {
      if (!currentUsuarioId) throw new Error("Usuário não autenticado ou sem vínculo em 'usuarios'");
      if (liked) {
        const { error } = await supabase
          .from("app_comment_likes")
          .delete()
          .eq("comment_id", c.id)
          .eq("usuario_id", currentUsuarioId);
        if (error) {
          await supabase
            .from("tarefas_comentarios_curtidas")
            .delete()
            .eq("comentario_id", c.id)
            .eq("usuario_id", currentUsuarioId);
        }
      } else {
        const { error } = await supabase
          .from("app_comment_likes")
          .upsert({ comment_id: c.id, usuario_id: currentUsuarioId }, { onConflict: "comment_id,usuario_id" });
        if (error) {
          await supabase
            .from("tarefas_comentarios_curtidas")
            .insert({ comentario_id: c.id, usuario_id: currentUsuarioId });
        }
      }
    } catch (e) {
      // reverte se falhar
      setLiked(v => !v);
      setLikes(n => (liked ? n + 1 : Math.max(0, n - 1)));
      console.error("toggle like failed", e);
    } finally {
      setLiking(false);
    }
  }

  // toggle checkbox centralizado
  const handleToggleAt = async (lineIdx: number) => {
    // Agora o toggle depende de canToggleTasks (padrão: true) e não mais de canEdit
    if (!canToggleTasks || (c.type ?? "texto") !== "texto") return;
    const nextText = toggleTaskAt(contentLocal, lineIdx);
    if (nextText === contentLocal) return;
    setContentLocal(nextText);          // feedback imediato
    try { await onSaveEdit(nextText); } // persiste
    catch (e) { console.error("toggle checkbox failed", e); }
  };

  // galeria + viewer interno
  const anexos = useMemo(() => normalizeAnexos(c.anexos), [c.anexos]);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  return (
    <div className={`rounded-lg border border-secondary dark:border-gray-700 bg-background dark:bg-gray-900 p-3 ${className}`}>
      {/* Cabeçalho */}
      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="text-xs text-gray-800 dark:text-white">
          {dataTxt} — <span className="font-medium">{c.authorName || "Usuário"}</span>
          {c.mentionBadge && (
            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-600 text-white align-middle">
              @ você
            </span>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-0">
          <button
            onClick={toggleLike}
            disabled={liking}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${liked ? "bg-pink-600 text-white" : "text-gray-700 dark:text-white hover:bg-black/5 dark:hover:bg-white/5"}`}
            title={liked ? "Descurtir" : "Curtir"}
            aria-label={liked ? "Descurtir" : "Curtir"}
          >
            <Heart className={`w-3.5 h-3.5 ${liked ? "fill-current" : ""}`} />
            <span>{likes}</span>
            <span className="hidden sm:inline">Curtir</span>
          </button>

          {canEdit && (c.type ?? "texto") === "texto" && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-800 dark:text-white hover:bg-black/5 dark:hover:bg-white/5"
              title="Editar"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Editar</span>
            </button>
          )}

          {canDelete && (
            <button
              onClick={async () => {
                if (confirmOnDelete) {
                  const ok = window.confirm("Excluir este comentário?");
                  if (!ok) return;
                }
                await onDelete();
              }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-800 dark:text-white hover:bg-black/5 dark:hover:bg-white/5"
              title="Excluir"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Excluir</span>
            </button>
          )}

          {onSendToDiary && (
            <button
              onClick={onSendToDiary}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-800 dark:text-white hover:bg-black/5 dark:hover:bg-white/5"
              title="Enviar para Diário de Obra"
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Diário</span>
            </button>
          )}
        </div>
      </div>

      {/* Corpo */}
      {(isEditing && (c.type ?? "texto") === "texto") ? (
        <DescricaoEditor
          value={draft}
          onChange={setDraft}
          showToolbar
          autoFocus
          minRows={3}
          maxRows={15}
          submitOnEnter={false}
          sendLabel="Salvar"
          onSubmitWithPayload={async ({ text }) => {
            await onSaveEdit(text);
            // Limpar rascunho após salvar com sucesso
            await clearDraft();
            setIsEditing(false);
          }}
        />
      ) : (
        <div className="prose dark:prose-invert text-sm text-gray-900 dark:text-white">
          {(contentLocal || "").split(/\r?\n/).map((line, idx) => {
            const meta = isTaskLine(line) ? parseTaskLine(line) : null;
            if (!meta) {
              return (
                <div key={idx} className="whitespace-pre-wrap">
                  <MentionRenderer texto={line} />
                </div>
              );
            }
            const { indent, checked, text } = meta;
            return (
              <div key={idx} className="flex items-start whitespace-pre-wrap">
                {indent ? <span className="whitespace-pre">{indent}</span> : null}
                <label className={`inline-flex items-start gap-2 dark:text-white ${canToggleTasks ? "cursor-pointer" : "cursor-default"}`}>
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4"
                    checked={checked}
                    disabled={!canToggleTasks}
                    onChange={() => handleToggleAt(idx)}
                    aria-label={checked ? "Marcar como não concluída" : "Marcar como concluída"}
                  />
                  <span className="flex-1">
                    <MentionRenderer texto={text || ""} />
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      )}

      {/* Anexos */}
      {Array.isArray(c.anexos) && c.anexos.length > 0 && (
        <div className="mt-2">
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {anexos.map((a, i) => {
              const fname = a.nome || a.url.split("/").pop() || "arquivo";
              const common = { onClick: () => setViewerIndex(i), title: fname };
              if (a.tipo === "imagem") {
                return (
                  <button key={`${fname}-${i}`} {...common} className="relative border rounded overflow-hidden hover:opacity-90 transition" type="button">
                    <img src={a.url} alt={fname} className="w-full h-16 object-cover" />
                  </button>
                );
              }
              if (a.tipo === "video") {
                return (
                  <button key={`${fname}-${i}`} {...common} className="relative border rounded overflow-hidden hover:opacity-90 transition bg-black flex items-center justify-center" type="button">
                    <Video className="w-6 h-6 text-white opacity-80" />
                  </button>
                );
              }
              // Documento/Outro: se for PDF, exibe miniatura; caso contrário, ícone genérico
              if (isPdf(a.url)) {
                return (
                  <button key={`${fname}-${i}`} {...common} className="relative border rounded overflow-hidden hover:opacity-90 transition bg-white" type="button">
                    <PdfThumb src={a.url} className="w-full h-16 object-cover" />
                    <span className="absolute bottom-0 right-0 m-1 px-1.5 py-0.5 text-[10px] rounded bg-black/70 text-white">PDF</span>
                  </button>
                );
              }
              return (
                <button key={`${fname}-${i}`} {...common} className="relative border rounded overflow-hidden hover:opacity-90 transition bg-gray-100 dark:bg-gray-800 flex items-center justify-center" type="button">
                  <FileIcon className="w-5 h-5" />
                </button>
              );
            })}
          </div>

          {viewerIndex !== null && (
            <Lightbox itens={anexos as any} index={viewerIndex} onClose={() => setViewerIndex(null)} onChange={setViewerIndex} />
          )}
        </div>
      )}
    </div>
  );
}
